import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useState } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { fetchFilterConfig, InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { validateString } from 'src/utils/string/stringUtil';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import {
	deleteSYS2020_R01,
	exportSYS2020_R01Excel,
	getSYS2020_R01AllCount,
	getSYS2020_R01List,
	patchSYS2020_R01,
	postSYS2020_R01,
} from './Api_SYS2020_R01';
import { defaultSYS2020_R01Dto, SYS2020_R01Dto } from './Dto_SYS2020_R01';
import useSYS2020_R01Store, { defaultSYS2020_R01Filter, SYS2020_R01Filter } from './Store_SYS2020_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS2020_R01Dto> | null>;
	setMessage: (message: string) => void;
}

type FetchMessageMode = 'show' | 'none';
interface FetchOptions { messageMode?: FetchMessageMode; }

export const Content_SYS2020_R01 = ({ gridRef, setMessage }: Props) => {
	const {
		SYS2020_R01Filter,
		SYS2020_R01PastFilter, setSYS2020_R01PastFilter,
		SYS2020_R01List, setSYS2020_R01List, insertSYS2020_R01List,
		deletedSYS2020_R01List, addDeletedSYS2020_R01List, removeDeletedSYS2020_R01List,
		selectedSYS2020_R01List, setSelectedSYS2020_R01List, removeSelectedSYS2020_R01List,
		setOriginalSYS2020_R01List,
		setAllCount, initData, setScrollTop,
	} = useSYS2020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const [clickable, setClickable] = useState(true);

	useFetchActionbarAuth('SYS2020_R01');

	const getParams = (filter: SYS2020_R01Filter): Partial<SYS2020_R01Filter> => ({
		commentCd: filter.commentCd || undefined,
		description: filter.description || undefined,
	});

	const defaultSYS2020_R01Row = useCallback(() => ({
		...defaultSYS2020_R01Dto,
		inserted: true,
	}), []);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		const { isDefaultFilter, listLength } = fetchFilterConfig(
			SYS2020_R01Filter,
			SYS2020_R01PastFilter,
			defaultSYS2020_R01Filter,
			SYS2020_R01List.length,
		);

		const response = await exportSYS2020_R01Excel(
			isDefaultFilter ? '' : 'search',
			getParams(SYS2020_R01Filter),
			listLength,
		);

		downloadSettingExcel({
			responseData: response.data.data,
			fileName: '언어코멘트등록-excel.xlsx',
		});
		setLoadingSpinner(false);
	}, [SYS2020_R01Filter, SYS2020_R01List.length, SYS2020_R01PastFilter, setLoadingSpinner]);

	const renewalAllCount = useCallback(async () => {
		const res = await getSYS2020_R01AllCount(getParams(SYS2020_R01Filter));
		setAllCount(res.data.data);
		return res.data.data;
	}, [SYS2020_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions,
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') setScrollTop(null);

		const initialListLength = SYS2020_R01List.length;
		if (fetchType === 'refetch') setSYS2020_R01List([]);

		const responseData = await getSYS2020_R01List(
			fetchType === 'refetch' ? 'refetch' : '',
			getParams(SYS2020_R01Filter),
			fetchType === 'refetch' ? initialListLength : SYS2020_R01List.length ?? 0,
		);

		const newList = [...responseData.data.data] as SYS2020_R01Dto[];
		const processAllCount = await renewalAllCount();

		setSYS2020_R01List(newList);
		setOriginalSYS2020_R01List(newList);
		setSelectedSYS2020_R01List([]);
		removeDeletedSYS2020_R01List(deletedSYS2020_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');

		if (newList.length === 0) {
			if (messageMode === 'show') showWarning('데이터가 없습니다.');
		} else if (messageMode === 'show') {
			ShowSuccessFetch(setMessage, `언어 코멘트 조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(''), 5000);
		}

		setSYS2020_R01PastFilter(SYS2020_R01Filter);
		setLoadingSpinner(false);
	}, [SYS2020_R01Filter, SYS2020_R01List, deletedSYS2020_R01List, gridRef, setMessage, setLoadingSpinner, setOriginalSYS2020_R01List, setSelectedSYS2020_R01List, removeDeletedSYS2020_R01List, renewalAllCount, setSYS2020_R01PastFilter, setScrollTop, setSYS2020_R01List]);

	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		const isGridInsert = rowIndex !== undefined;
		InsertRowHandler({
			event: e,
			dataList: SYS2020_R01List,
			rowData: isGridInsert
				? SYS2020_R01List[rowIndex]
				: { ...defaultSYS2020_R01Row(), valueIndex: -1 },
			insertRow: insertSYS2020_R01List,
			defaultRowData: defaultSYS2020_R01Row(),
			...(isGridInsert && { setClickable }),
		});
	}, [SYS2020_R01List, insertSYS2020_R01List, defaultSYS2020_R01Row, setClickable]);

	const validateInsertData = (data: SYS2020_R01Dto, currentRowIndex: number) => {
		if (!validateString(data.commentCd)) {
			showWarning('코드를 입력해주세요.');
			return false;
		}
		if (!validateString(data.description)) {
			showWarning('용도를 입력해주세요.');
			return false;
		}

		const activeRows = SYS2020_R01List.filter(
			(row) => !deletedSYS2020_R01List.includes(row.valueIndex.toString()),
		);
		const otherRows = activeRows.filter((_, index) => index !== currentRowIndex);
		if (otherRows.some((row) => row.commentCd === data.commentCd)) {
			showWarning(`중복된 코드가 있습니다.\n코드: ${data.commentCd}`);
			return false;
		}
		return true;
	};

	const handleSaveClick = useCallback(async () => {
		const insertedList = SYS2020_R01List.filter(
			(row) => !deletedSYS2020_R01List.includes(row.valueIndex.toString()) && row.inserted,
		);
		const modifiedList = SYS2020_R01List.filter(
			(row) => !deletedSYS2020_R01List.includes(row.valueIndex.toString()) && row.isModified && !row.inserted,
		);

		let valid = true;
		insertedList.forEach((value) => {
			const index = SYS2020_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateInsertData(value, index)) valid = false;
		});
		if (!valid) return;

		modifiedList.forEach((value) => {
			const index = SYS2020_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateInsertData(value, index)) valid = false;
		});
		if (!valid) return;

		const deleteStringList = new Set(deletedSYS2020_R01List.map((item) => item.toString()));
		const deleteTargetList = SYS2020_R01List.filter((item) =>
			deleteStringList.has(item.valueIndex.toString()),
		);

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postSYS2020_R01(insertedList) : Promise.resolve(undefined),
			modifiedList.length > 0 ? patchSYS2020_R01(modifiedList) : Promise.resolve(undefined),
			deleteTargetList.length > 0
				? Promise.all(deleteTargetList.map((value) => deleteSYS2020_R01(value.commentCd)))
				: Promise.resolve(undefined),
		]);

		if (insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('언어 코멘트 저장이 완료되었습니다.');
			setMessage('언어 코멘트 저장이 완료되었습니다.');
		} else if (deleteResponse) {
			showSuccess('언어 코멘트 삭제가 완료되었습니다.');
			setMessage('언어 코멘트 삭제가 완료되었습니다.');
		}
		setTimeout(() => setMessage(''), 5000);

		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [SYS2020_R01List, deletedSYS2020_R01List, executeDataFetch, setMessage]);

	const handleDeleteClick = useCallback(() => {
		if (deletedSYS2020_R01List.length > 0) {
			removeDeletedSYS2020_R01List(deletedSYS2020_R01List);
			removeSelectedSYS2020_R01List();
			setSYS2020_R01List([...SYS2020_R01List]);
		} else if (selectedSYS2020_R01List.length > 0) {
			addDeletedSYS2020_R01List(selectedSYS2020_R01List);
			removeSelectedSYS2020_R01List(selectedSYS2020_R01List);
		}
	}, [SYS2020_R01List, deletedSYS2020_R01List, selectedSYS2020_R01List, addDeletedSYS2020_R01List, removeDeletedSYS2020_R01List, removeSelectedSYS2020_R01List, setSYS2020_R01List]);

	return {
		clickable, setClickable,
		handleExcelClick,
		defaultSYS2020_R01Row,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	};
};
